import { useState, useRef, useEffect } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { User, LogIn, LayoutDashboard, Settings, LogOut, Loader2, GraduationCap, Bell, Check, Trash2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import axiosClient from '../../api/axiosClient';
import EditProfileModal from './EditProfileModal';

export default function Navbar({ onLoginClick, onTrialClick }) {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [clientNotifications, setClientNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('trialToken'));
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const notifRef = useRef(null);
  
  useEffect(() => {
    const handleAuthChange = () => setIsLoggedIn(!!localStorage.getItem('trialToken'));
    window.addEventListener('auth-change', handleAuthChange);
    return () => window.removeEventListener('auth-change', handleAuthChange);
  }, []);

  const fetchNavbarData = () => {
    if (isLoggedIn) {
      axiosClient.get('/trials/me')
        .then(res => setProfileData(res.data.profile))
        .catch(err => {
          console.error("Failed to load profile", err);
          if (err.response?.status === 401) {
            handleLogout();
          }
        });
        
      axiosClient.get('/trials/me/messages')
        .then(res => setMessages(res.data.messages || []))
        .catch(err => console.error("Failed to load messages", err));
        
      axiosClient.get('/trials/me/notifications')
        .then(res => setClientNotifications(res.data.notifications || []))
        .catch(err => console.error("Failed to load notifications", err));
    }
  };

  useEffect(() => {
    fetchNavbarData();
  }, [isLoggedIn]);

  useEffect(() => {
    window.addEventListener('focus', fetchNavbarData);
    window.addEventListener('messages-updated', fetchNavbarData);
    return () => {
      window.removeEventListener('focus', fetchNavbarData);
      window.removeEventListener('messages-updated', fetchNavbarData);
    };
  }, [isLoggedIn]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfileClick = () => {
    if (isLoggedIn) {
      setShowProfileMenu(!showProfileMenu);
    } else {
      onLoginClick();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('trialToken');
    setProfileData(null);
    setMessages([]);
    setShowProfileMenu(false);
    window.dispatchEvent(new Event('auth-change'));
    navigate('/');
  };

  const handleAccessSandbox = async () => {
    try {
      if (!profileData?.email) {
        toast.error("User data not loaded yet.");
        return;
      }
      const res = await axiosClient.post('/trials/sandbox-redirect', { clientEmail: profileData.email });
      if (res.data.redirectUrl) {
        window.location.href = res.data.redirectUrl;
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to access sandbox. It might be pending or expired.");
    }
  };

  const getLinkClasses = ({ isActive }) => 
    isActive 
      ? "nav-link-item bg-primary-50 text-primary-600 px-4 py-2 rounded-lg font-medium transition-all"
      : "nav-link-item text-slate-600 font-medium hover:text-slate-900 px-4 py-2 transition-colors";

  return (
    <>
      <nav className="glass-nav">
        <div className="nav-container max-w-7xl mx-auto px-8 w-full flex justify-between items-center py-4">
        <Link to="/" className="brand-logo" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700, fontSize: '1.5rem', color: 'var(--dark)' }}>
          <img src="/logo.png" alt="Roriri Logo" style={{ height: '32px' }} />
          <span>  ERP</span>
        </Link>

        {/* Navigation Links */}
        <div className="nav-links flex gap-2 items-center hidden md:flex">
          <NavLink to="/" end className={getLinkClasses}>Home</NavLink>
          <NavLink to="/features" className={getLinkClasses}>Features</NavLink>
          <NavLink to="/pricing" className={getLinkClasses}>Pricing</NavLink>
          <NavLink to="/faq" className={getLinkClasses}>FAQ</NavLink>
          <NavLink to="/contact" className={getLinkClasses}>Contact</NavLink>
          
          <div className="flex gap-4 items-center ml-4 border-l border-slate-200 pl-6">
            {!isLoggedIn && (
              <>
                <button 
                  onClick={onLoginClick}
                  className="group relative inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-slate-700 transition-all duration-300 ease-in-out bg-white border border-slate-200 rounded-full shadow-sm hover:border-primary-300 hover:text-primary-600 hover:shadow-md hover:shadow-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 overflow-hidden"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <LogIn size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    Login
                  </span>
                  <div className="absolute inset-0 h-full w-full bg-gradient-to-tr from-primary-50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={onTrialClick}
                  style={{ padding: '0.6rem 1.4rem' }}
                >
                  Start Free Trial
                </button>
              </>
            )}
            {isLoggedIn && (
              <div className="relative" ref={notifRef}>
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2.5 text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center justify-center mr-2"
                >
                  <Bell size={20} />
                  {clientNotifications.filter(n => !n.is_read).length > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-primary-500 rounded-full border border-white"></span>
                  )}
                </button>
                
                <AnimatePresence>
                  {showNotifications && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute top-full mt-2 right-0 w-80 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50 flex flex-col max-h-[400px]"
                    >
                      <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center shrink-0">
                        <h3 className="font-bold text-slate-900">Notifications</h3>
                        <div className="flex gap-2">
                          <button 
                            onClick={async () => {
                              try {
                                await axiosClient.put('/trials/me/notifications/read', {});
                                fetchNavbarData();
                              } catch(e) {}
                            }} 
                            className="text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1" title="Mark all as read"
                          >
                            <Check size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="overflow-y-auto flex-1 p-2">
                        {clientNotifications.length === 0 ? (
                          <div className="p-4 text-center text-sm text-slate-500">
                            No notifications in the last 7 days.
                          </div>
                        ) : (
                          clientNotifications.map(n => (
                            <div key={n.id} className={`flex items-start gap-3 p-3 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer border-b border-slate-50 last:border-0 ${!n.is_read ? 'bg-primary-50/30' : ''}`} 
                               onClick={async () => {
                                 if(!n.is_read) {
                                   try {
                                     await axiosClient.put('/trials/me/notifications/read', { id: n.id });
                                     fetchNavbarData();
                                   } catch(e) {}
                                 }
                               }}>
                              <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${!n.is_read ? 'bg-primary-500' : 'bg-transparent'}`} />
                              <div>
                                <p className={`text-sm ${!n.is_read ? 'font-medium text-slate-900' : 'text-slate-600'}`}>{n.message}</p>
                                <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">{new Date(n.created_at).toLocaleString()}</p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            
            <div className="relative" ref={menuRef}>
              <button 
                onClick={handleProfileClick}
                className="relative p-2.5 text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ml-1 flex items-center justify-center"
                aria-label="Profile"
                title="Profile"
              >
                <User size={20} />
                {messages.some(m => !m.client_read) && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
                )}
              </button>
              
              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-100 py-2 z-50">
                  {profileData && (
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-sm font-medium text-slate-900 truncate">Hello, {profileData.name}</p>
                      <p className="text-xs text-slate-500 truncate">{profileData.email}</p>
                    </div>
                  )}
                  
                  <button 
                    onClick={() => {
                      setShowProfileMenu(false);
                      handleAccessSandbox();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <LayoutDashboard size={16} className="text-primary-600" /> 
                    <span className="font-medium">Access Sandbox</span>
                  </button>
                  
                  <Link 
                    to="/messages"
                    onClick={() => setShowProfileMenu(false)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <div className="relative">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                      {messages.some(m => !m.client_read) && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                      )}
                    </div>
                    <span className="font-medium flex-1 text-left">Messages</span>
                    {messages.some(m => !m.client_read) && (
                      <span className="bg-red-100 text-red-600 py-0.5 px-2 rounded-full text-xs font-bold">
                        {messages.filter(m => !m.client_read).length}
                      </span>
                    )}
                  </Link>

                  <Link 
                    to="/subscriptions"
                    onClick={() => setShowProfileMenu(false)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>
                    <span className="font-medium flex-1 text-left">Subscriptions & Payments</span>
                  </Link>
                  
                  <button 
                    onClick={() => {
                      setShowProfileMenu(false);
                      setShowEditModal(true);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors border-b border-slate-100"
                  >
                    <Settings size={16} className="text-slate-500" /> 
                    <span className="font-medium">Edit Profile Info</span>
                  </button>

                  <button 
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors rounded-b-xl"
                  >
                    <LogOut size={16} /> 
                    <span className="font-medium">Log out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </nav>
      
      {showEditModal && (
        <EditProfileModal 
          profileData={profileData} 
          onClose={() => setShowEditModal(false)} 
          onProfileUpdated={(newData) => setProfileData(prev => ({...prev, ...newData}))} 
        />
      )}
    </>
  );
}
